import {FC, ReactNode} from 'react'
import {useIntl} from 'react-intl'
import {CSRTable} from './table-cr/CSRTable'
import {Navigate, NavLink, Outlet, Route, Routes, useParams} from 'react-router-dom'
import {KTCard} from '../../template/helpers'
import {CSRHeader} from './components/CSRHeader'
import {appsAPI} from '../../services/AppsService'
import {PageTitle} from '../../template/layout/core'
import {EmptyCSRPage} from './components/EmptyCSRPage'
import {LrTable} from './table-lr/LrTable'
import {ErTable} from './table-er/ErTable'
import {LrHeader} from './components/LrHeader'
import {ErHeader} from './components/ErHeader'
import {HrHeader} from './components/HrHeader'
import {HrTable} from './table-hr/HrTable'
import {useRedirectOnAccountSwitch} from '../../hooks/useRedirectOnAccountSwitch'

const CSRLayout: FC = () => {
  const intl = useIntl()
  return (
    <div className='d-flex flex-column flex-lg-row 888'>
      <div className='flex-lg-row-fluid ms-lg-5'>
        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 fw-bolder flex-nowrap mb-5'>
          <li className='nav-item'>
            <NavLink className='nav-link text-muted text-active-primary text-hover-primary' to='calls/'>
              <small className='text-uppercase'>{intl.formatMessage({id: 'APPS.CSR'})}</small>
            </NavLink>
          </li>
          <li className='nav-item'>
            <NavLink className='nav-link text-muted text-active-primary text-hover-primary' to='lines/'>
              <small className='text-uppercase'>{intl.formatMessage({id: 'APPS.CSL'})}</small>
            </NavLink>
          </li>
          <li className='nav-item'>
            <NavLink className='nav-link text-muted text-active-primary text-hover-primary' to='employees/'>
              <small className='text-uppercase'>{intl.formatMessage({id: 'APPS.CER'})}</small>
            </NavLink>
          </li>
          <li className='nav-item'>
            <NavLink className='nav-link text-muted text-active-primary text-hover-primary' to='hourly/'>
              <small className='text-uppercase'>{intl.formatMessage({id: 'APPS.CHR'})}</small>
            </NavLink>
          </li>
        </ul>
        <Outlet />
      </div>
    </div>
  )
}

type WithChildren = {
  children?: ReactNode
}

export const CSRTableWrap: FC<WithChildren> = ({children}) => {
  return <KTCard className='shadow-sm'>{children}</KTCard>
}

const CSRModule: FC = () => {
  const intl = useIntl()
  const {appId} = useParams()
  useRedirectOnAccountSwitch('/csr/')
  const {data: app, isLoading, error} = appsAPI.useFetchAppByIdQuery(appId!)
  if (app === null || error) {
    return <EmptyCSRPage error={error} />
  }

  return (
    <>
      {isLoading ? (
        <>
          <PageTitle>{intl.formatMessage({id: 'APPS.CDR'})}</PageTitle>
          <span className='indicator-progress' style={{display: 'block'}}>
            {intl.formatMessage({id: 'WAIT'})}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        </>
      ) : (
        <Routes>
          <Route element={<CSRLayout />}>
            <Route index element={<Navigate to='calls/' replace />} />
            <Route
              path='calls/'
              element={
                <>
                  <PageTitle>{intl.formatMessage({id: 'APPS.CSR'}) + ' - ' + app?.crm_domain}</PageTitle>
                  <CSRTableWrap>
                    <CSRHeader />
                    <CSRTable />
                  </CSRTableWrap>
                </>
              }
            />
            <Route
              path='lines/'
              element={
                <>
                  <PageTitle>{intl.formatMessage({id: 'APPS.CSL'}) + ' - ' + app?.crm_domain}</PageTitle>
                  <CSRTableWrap>
                    <LrHeader />
                    <LrTable />
                  </CSRTableWrap>
                </>
              }
            />
            <Route
              path='employees/'
              element={
                <>
                  <PageTitle>{intl.formatMessage({id: 'APPS.CER'}) + ' - ' + app?.crm_domain}</PageTitle>
                  <CSRTableWrap>
                    <ErHeader />
                    <ErTable />
                  </CSRTableWrap>
                </>
              }
            />
            <Route
              path='hourly/'
              element={
                <>
                  <PageTitle>{intl.formatMessage({id: 'APPS.CHR'}) + ' - ' + app?.crm_domain}</PageTitle>
                  <CSRTableWrap>
                    <HrHeader />
                    <HrTable />
                  </CSRTableWrap>
                </>
              }
            />
            <Route path='*' element={<Navigate to='/error/404' />} />
          </Route>
        </Routes>
      )}
    </>
  )
}

export default CSRModule
